import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Button } from '@mui/material';

interface ArrayEditorProps {
  data: string[][];
  onChange: (newData: string[][]) => void;
  onTableBlur?: (newData: string[][]) => void;
}

const ArrayEditor: React.FC<ArrayEditorProps> = ({
  data,
  onChange,
  onTableBlur
}) => {
  const [selectedCell, setSelectedCell] = useState<[number, number] | null>(null);
  const [editValue, setEditValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle cell click
  const handleCellClick = (row: number, col: number) => {
    setSelectedCell([row, col]);
    setEditValue(data[row][col]);
  };

  // Handle value change
  const handleValueChange = (value: string) => {
    if (selectedCell) {
      const [row, col] = selectedCell;
      data[row][col] = value;
      onChange(data);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!selectedCell) return;

    const [row, col] = selectedCell;
    let newRow = row;
    let newCol = col;

    switch (e.key) {
      case 'ArrowRight':
        newCol = Math.min(col + 1, data[0].length - 1);
        break;
      case 'ArrowLeft':
        newCol = Math.max(col - 1, 0);
        break;
      case 'ArrowUp':
        newRow = Math.max(row - 1, 0);
        break;
      case 'ArrowDown':
        newRow = Math.min(row + 1, data.length - 1);
        break;
      case 'Enter':
        handleValueChange(editValue);
        setSelectedCell(null);
        return;
      case 'Escape':
        setSelectedCell(null);
        return;
    }

    if (newRow !== row || newCol !== col) {
      setSelectedCell([newRow, newCol]);
      setEditValue(data[newRow][newCol]);
    }
  };

  // Focus input when cell is selected
  useEffect(() => {
    if (selectedCell && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedCell]);

  const handleTableBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // Only trigger if focus is leaving the table, not just moving between cells
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      onTableBlur?.(data);
    }
  };

  return (
    <Container
    tabIndex={-1}
    onBlur={handleTableBlur}
    >
      <TableWrapper>
        <Table>
          <tbody>
            {data?.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row?.map((cell, colIndex) => (
                  <Cell
                    key={colIndex}
                    isSelected={selectedCell?.[0] === rowIndex && selectedCell?.[1] === colIndex}
                    onClick={() => handleCellClick(rowIndex, colIndex)}
                  >
                    {selectedCell?.[0] === rowIndex && selectedCell?.[1] === colIndex ? (
                      <Input
                        ref={inputRef}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={() => {
                          handleValueChange(editValue);
                          setSelectedCell(null);
                        }}
                      />
                    ) : (
                      cell
                    )}
                  </Cell>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  display: inline-block;
`;

const TableWrapper = styled.div`
  display: inline-block;
  overflow-x: auto;
  overflow-y: hidden;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const Table = styled.table`
  border-collapse: collapse;
  table-layout: fixed;
  display: inline-block;
`;

const Cell = styled.td<{ 
     isSelected: boolean }>`

  width: 120px;
  max-width: 150px;
  min-width: 80px;
  height: 40px;
  padding: 0 8px;
  border: 1px solid #dee2e6;
  background: ${props => props.isSelected ? '#e7f5ff' : 'white'};
  transition: background-color 0.2s;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: ${props => props.isSelected ? '#e7f5ff' : '#f8f9fa'};
  }
`;

const Input = styled.input`
  width: 120px;
  max-width: 150px;
  min-width: 80px;
  padding: 8px;
  border: none;
  background: transparent;
  font-size: inherit;
  font-family: inherit;
  outline: none;
`;

// Usage example:
export const TableLocatorInput = ({data, onChange, onTableBlur}: {data: string[][], onChange: (newData: string[][]) => void, onTableBlur: (newData: string[][]) => void}) => {
  const addRow = () => {
    onChange([...(data || []), Array(data?.[0]?.length || 1).fill('')]);
  };

  const addColumn = () => {
    onChange(data?.map(row => [...row, '']));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            onClick={addRow}
            style={{
              background: '#FFFFFF',
              color: '#6435C9',
              border: '1px solid #6435C9',
              borderRadius: '4px',
              padding: '6px 12px',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            Add Row
          </Button>
          <Button
            size="small"
            disabled={data?.length === 0}
            onClick={addColumn}
            style={{
              background: '#FFFFFF',
              color: '#6435C9',
              border: '1px solid #6435C9',
              borderRadius: '4px',
              padding: '6px 12px',
              fontSize: '14px',
              fontWeight: 500
            }}
          >
            Add Column
          </Button>
        </div>
        <div style={{ 
          color: '#718096',
          fontSize: '14px',
          fontWeight: 500
        }}>
          {data?.[0].length} columns
        </div>
      </div>

      <div style={{ 
        width: '100%', 
        height: `calc(60*${data?.length || 1}px)`,
        position: 'relative',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'auto'
        }}>
          <ArrayEditor
            data={data}
            onChange={onChange}
            onTableBlur={onTableBlur}
          />
        </div>
      </div>
    </div>
  );
};